"use client"

import React, { useState, useEffect } from "react"
import { X, Save, Download } from "lucide-react"
import { Dialog, Flex, Box, Heading, Text, TextField, Select, Button, TextArea } from "@radix-ui/themes"
import { useLanguage } from "../contexts/LanguageContext"

interface Table {
  _id?: string
  tableNumber: string
  capacity: number
  location: "indoor" | "outdoor" | "private"
  notes?: string
}

interface TableFormData {
  tableNumber: string
  capacity: number
  location: "indoor" | "outdoor" | "private"
  notes: string
}

interface TableModalProps {
  isOpen: boolean
  onClose: () => void
  table?: Table | null
  restaurantId: string
  onSubmit: (tableId: string | TableFormData, data?: TableFormData) => Promise<void>
}

const TableModal: React.FC<TableModalProps> = ({ isOpen, onClose, table, restaurantId, onSubmit }) => {
  const { t } = useLanguage()
  const [formData, setFormData] = useState<TableFormData>({
    tableNumber: "",
    capacity: 4,
    location: "indoor",
    notes: "",
  })

  useEffect(() => {
    if (table) {
      setFormData({
        tableNumber: table.tableNumber,
        capacity: table.capacity,
        location: table.location,
        notes: table.notes || "",
      })
    } else {
      setFormData({
        tableNumber: "",
        capacity: 4,
        location: "indoor",
        notes: "",
      })
    }
  }, [table])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      if (table?._id) {
        // Update existing table
        await onSubmit(table._id, formData)
      } else {
        // Create new table
        await onSubmit(formData)
      }
      onClose()
    } catch (error) {
      console.error("Error saving table:", error)
    }
  }

  if (!isOpen) return null

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Content style={{ maxWidth: 500 }}>
        <Dialog.Title>
          {table ? t("editTable") : t("addNewTable")}
        </Dialog.Title>

        <Dialog.Description size="2" mb="4">
          {table ? t("updateTableInfo") : t("createNewTableDescription")}
        </Dialog.Description>

        <form onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("tableNumberLabel")}
            </label>
            <input
              type="text"
              value={formData.tableNumber}
              onChange={(e) => setFormData({ ...formData, tableNumber: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("capacity")}
            </label>
            <input
              type="number"
              min="1"
              max="20"
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("location")}
            </label>
            <select
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value as "indoor" | "outdoor" | "private" })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="indoor">{t("indoor")}</option>
              <option value="outdoor">{t("outdoor")}</option>
              <option value="private">{t("private")}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("notesOptional")}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <Flex gap="3" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  {t("cancel")}
                </Button>
              </Dialog.Close>
              <Button type="submit">
                <Flex align="center" gap="2">
                  <Save className="w-4 h-4" />
                  {table ? t("updateTable") : t("createTable")}
                </Flex>
              </Button>
            </Flex>
          </div>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default TableModal 